import { useEffect, useState } from "react";
import { Share, X } from "lucide-react";

const DISMISS_KEY = "ios-pwa-install-hint-dismissed";

/**
 * IosInstallHint — a one-time bottom-anchored hint banner shown only to
 * iOS Safari users (the one major browser that doesn't fire
 * `beforeinstallprompt`, so the navbar `<PwaInstallButton>` never appears).
 *
 * Walks the pilot through the 2-step home-screen install flow:
 *   1. Tap the Share icon (square with up-arrow) in Safari's bottom bar.
 *   2. Choose "Add to Home Screen".
 *
 * Hides automatically when:
 *   - already running in standalone mode (already installed)
 *   - the pilot has dismissed the banner once (`localStorage` flag)
 *   - not iOS Safari (Chrome/Edge/Firefox on iOS use the system share
 *     sheet too but their PWAs are heavily neutered, so we keep the hint)
 */
export default function IosInstallHint() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    // Already installed → never show.
    const standalone =
      window.matchMedia?.("(display-mode: standalone)")?.matches
      || window.navigator.standalone === true;
    if (standalone) return;
    // Already dismissed → never show again.
    try {
      if (window.localStorage.getItem(DISMISS_KEY) === "1") return;
    } catch {
      // localStorage blocked (private mode etc.) — fail safe and skip
      // showing rather than nag every page load.
      return;
    }
    // Detect iOS Safari. iPad/iPhone/iPod, and exclude in-app webviews
    // (those have "FBAN", "Instagram", etc. in the UA — no PWA install
    // possible there anyway).
    const ua = window.navigator.userAgent || "";
    const isIos = /iPad|iPhone|iPod/.test(ua) || (
      // iPadOS 13+ reports as MacIntel with touch support.
      ua.includes("Mac") && navigator.maxTouchPoints > 1
    );
    const isWebView = /FBAN|FBAV|Instagram|Line\/|Twitter|MicroMessenger/.test(ua);
    if (!isIos || isWebView) return;
    // Defer show by ~2.5s so we don't fight with the auth flow on first
    // page load.
    const t = setTimeout(() => setShow(true), 2500);
    return () => clearTimeout(t);
  }, []);

  if (!show) return null;

  const dismiss = () => {
    setShow(false);
    try {
      window.localStorage.setItem(DISMISS_KEY, "1");
    } catch {
      /* ignore */
    }
  };

  return (
    <div
      className="fixed bottom-3 left-1/2 -translate-x-1/2 z-50 max-w-[92vw] sm:max-w-md w-full px-3"
      data-testid="ios-install-hint"
    >
      <div className="glass border border-[#00ffff]/40 bg-black/80 backdrop-blur-md p-3 sm:p-4 flex items-start gap-3">
        <div className="flex-none w-9 h-9 sm:w-10 sm:h-10 rounded border border-[#00ffff]/50 bg-[#00ffff]/10 flex items-center justify-center">
          <Share className="w-4 h-4 sm:w-5 sm:h-5 text-[#00ffff]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-mono-cosmic text-[9px] sm:text-[10px] tracking-[0.35em] uppercase text-[#00ffff] mb-1">
            Install on iPhone
          </div>
          <p className="font-mono-cosmic text-[11px] sm:text-xs leading-snug text-white/85">
            Tap the <Share className="w-3 h-3 inline-block -mt-0.5 mx-0.5 text-[#00ffff]" /> share icon below, then choose
            {" "}<span className="text-[#ff007f] font-semibold">"Add to Home Screen"</span> for full-screen offline play.
          </p>
        </div>
        <button
          onClick={dismiss}
          className="flex-none -m-1 p-2 text-white/50 hover:text-white transition"
          data-testid="ios-install-hint-dismiss"
          aria-label="Dismiss install hint"
          title="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
